import {
  isStepColumn,
  parseStepColumnKey,
  stepColumnKey,
} from '../components/ApplicantsTable/stepColumns'

import { defaultColumnVisibility, defaultColumnOrder } from './constants'

// Mapping from frontend column keys to backend enum values
export const COLUMN_KEY_TO_BACKEND_ENUM: Record<string, string | undefined> = {
  applicant: 'APPLICANT_NAME',
  position: 'JOB_LISTING',
  employees: 'RECRUITER',
  contact: 'PHONE',
  email: 'APPLICANT_EMAIL',
  calls: 'CALL_COUNT',
  missedCalls: 'MISSED_CALL_COUNT',
  applied: 'APPLICATION_DATE',
  lastContact: 'LAST_CONTACT_DATE',
  hired: 'HIRED_DATE',
  terminated: 'TERMINATED_DATE',
  disqualified: 'DISQUALIFIED_DATE',
  notInterested: 'NOT_INTERESTED_DATE',
  availableForWork: 'AVAILABLE_FOR_WORK_DATE',
  stage: 'APPLICANT_STAGE',
  experience: 'EXPERIENCE',
  location: 'LOCATION',
  actions: 'TAGS',
  leadSource: 'LEAD_SOURCE',
}

// Mapping from backend enum values to frontend column keys
export const BACKEND_ENUM_TO_COLUMN_KEY = {
  APPLICANT_NAME: 'applicant',
  JOB_LISTING: 'position',
  RECRUITER: 'employees',
  PHONE: 'contact',
  APPLICANT_EMAIL: 'email',
  CALL_COUNT: 'calls',
  MISSED_CALL_COUNT: 'missedCalls',
  APPLICATION_DATE: 'applied',
  APPLICANT_STAGE: 'stage',
  EXPERIENCE: 'experience',
  AVAILABLE_FOR_WORK_DATE: 'availableForWork',
  TAGS: 'actions',
  HIRING_STAGE: 'stage',
  LAST_CONTACT_DATE: 'lastContact',
  HIRED_DATE: 'hired',
  TERMINATED_DATE: 'terminated',
  DISQUALIFIED_DATE: 'disqualified',
  NOT_INTERESTED_DATE: 'notInterested',
  LEAD_SOURCE: 'leadSource',
  LOCATION: 'location',
}

export const buildPresetColumns = (
  order: string[],
  visibility: Record<string, boolean>
) => {
  const columns: Array<{
    column: string
    order: number
    customFieldId?: string
    stepType?: string
    customStepId?: string
  }> = []

  order.forEach((key) => {
    if (key === 'select' || !visibility[key]) {
      return
    }

    // Check if this is a custom field column
    if (key.startsWith('customField_')) {
      const customFieldId = key.replace('customField_', '')
      columns.push({
        column: 'CUSTOM_FIELD',
        customFieldId: customFieldId,
        order: columns.length + 1,
      })
      return
    }

    // Check if this is a step column (enum step type or custom step)
    if (isStepColumn(key)) {
      const { type, customStepId } = parseStepColumnKey(key)
      columns.push({
        column: 'STEP',
        stepType: type,
        ...(customStepId && { customStepId }),
        order: columns.length + 1,
      })
      return
    }

    // Handle standard columns
    const backendColumn = COLUMN_KEY_TO_BACKEND_ENUM[key]

    if (!backendColumn) {
      return
    }

    columns.push({
      column: backendColumn,
      order: columns.length + 1,
    })
  })

  return columns
}

export const loadColumnsFromPreset = (
  presetColumns: Array<{
    column: string
    order: number
    customFieldId?: string
    stepType?: string
    customStepId?: string
  }>,
  currentColumnVisibility: Record<string, boolean>,
  setColumnVisibility: (visibility: Record<string, boolean>) => void,
  setColumnOrder: (order: string[]) => void
) => {
  // If no columns in preset, show all columns by default
  if (!presetColumns || presetColumns.length === 0) {
    setColumnVisibility(defaultColumnVisibility)
    setColumnOrder(defaultColumnOrder)
    return
  }

  // Create new visibility object - start with all false except select
  const newVisibility = Object.keys(currentColumnVisibility).reduce(
    (acc, key) => {
      acc[key] = key === 'select' ? true : false
      return acc
    },
    {}
  )

  // Create new order array
  const newOrder = ['select'] // Always include select first

  // Sort preset columns by order and map to frontend keys
  const sortedPresetColumns = [...presetColumns].sort(
    (a, b) => a.order - b.order
  )

  sortedPresetColumns.forEach((presetCol) => {
    let frontendKey: string | undefined

    // Handle custom field columns
    if (presetCol.column === 'CUSTOM_FIELD' && presetCol.customFieldId) {
      frontendKey = `customField_${presetCol.customFieldId}`
    } else if (presetCol.column === 'STEP' && presetCol.stepType) {
      // Handle step columns (enum step type or custom step)
      frontendKey = stepColumnKey(presetCol.stepType, presetCol.customStepId)
    } else {
      // Handle standard columns
      frontendKey = BACKEND_ENUM_TO_COLUMN_KEY[presetCol.column]
    }

    if (frontendKey && frontendKey !== 'select') {
      newVisibility[frontendKey] = true
      if (!newOrder.includes(frontendKey)) {
        newOrder.push(frontendKey)
      }
    }
  })

  // Add any remaining columns that weren't in the preset (as hidden)
  Object.keys(currentColumnVisibility).forEach((key) => {
    if (!newOrder.includes(key) && key !== 'select') {
      newOrder.push(key)
    }
  })

  // Update state only (don't save to localStorage when loading from preset)
  setColumnVisibility(newVisibility)
  setColumnOrder(newOrder)
}
